import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import type { ChatDocGroupRow } from '../../types';

// ── Admin → Chat: which groups are held at the top of the chat list (report f4c8e029) ───────────────────────────
// Calem: "Allow admin to pin certain groups, up to 4, at the top of the list." Asked whether a pin is one
// administrator's own order or the firm's, he answered that any of the administrators may set or change them "and
// yes these groups should appear on everyone's chat list at the top" — so this card is firm-wide: what is ticked
// here is what every member of a pinned group finds at the top of their own chat list.
// The second card of this tab, under the document-upload one, and the same list of groups: nothing about a group
// is written into the code, so the next firm pins whichever of their own groups they like. The server keeps the
// picks as one app_settings value (routes/chat.ts 'chat_pins') — no table, no column — and refuses a fifth, which
// is why the boxes below go dead once four are ticked.
// Saved on the press, as the radio above it is: there is nothing to Save but the ticking itself.
// The group rows read the same three lines as the card above them (cdg_unnamed / cdg_members / cdg_made_by):
// one group is one group, however it is being chosen.

export default function ChatPins() {
  const { t } = useTranslation();
  const [groups, setGroups] = useState<ChatDocGroupRow[] | null>(null);
  const [ids, setIds] = useState<string[]>([]);      // in pin order: the first is the top row of the chat list
  const [max, setMax] = useState(4);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.chatPins()
      .then(d => { setGroups(d.groups || []); setIds(d.ids || []); setMax(d.max || 4); })
      .catch((e: any) => { setGroups([]); setErr(e.message); });
  }, []);

  // A tick goes on the end of the order, so the ticking itself is the order they sit in; an untick closes up.
  const toggle = async (id: string) => {
    if (busy) return;
    const on = ids.includes(id);
    if (!on && ids.length >= max) return;
    const was = ids;
    const next = on ? ids.filter(x => x !== id) : [...ids, id];
    setIds(next); setErr(''); setDone(false); setBusy(true);
    try { const r = await api.chatSetPins(next); setIds(r.ids || []); setDone(true); }
    catch (e: any) { setIds(was); setErr(e.message); }
    finally { setBusy(false); }
  };

  const full = ids.length >= max;
  const line = (dead: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '9px 2px',
    borderTop: '1px solid var(--border)', fontSize: 12.5,
    color: dead ? 'var(--text-tertiary)' : 'var(--text-primary)',
    cursor: busy ? 'wait' : dead ? 'default' : 'pointer',
    opacity: dead ? 0.6 : 1,
  });
  const rank: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, flexShrink: 0,
    borderRadius: 11, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)',
    fontSize: 11.5, fontWeight: 700, color: 'var(--text-secondary)',
  };

  return (
    <div style={{ maxWidth: 820, margin: '16px auto 0' }}>
      <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 16, background: 'var(--bg-surface)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          <i className="ti ti-pin" style={{ fontSize: 20, color: 'var(--text-secondary)' }} aria-hidden />
          <div style={{ flex: 1, fontSize: 14, fontWeight: 800, color: 'var(--text-primary)' }}>{t('cpn_title')}</div>
          <span style={{ fontSize: 12, fontWeight: 600, color: full ? '#B45309' : 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
            {t('cpn_count', { n: ids.length, max })}
          </span>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 12 }}>{t('cpn_desc', { max })}</div>

        {groups === null ? (
          !err && <div style={{ padding: 12, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" aria-hidden /></div>
        ) : (
          <>
            {groups.map(g => {
              const at = ids.indexOf(g.id);
              const on = at >= 0;
              return (
                <label key={g.id} style={line(!on && full)}>
                  <input type="checkbox" checked={on} onChange={() => toggle(g.id)} disabled={busy || (!on && full)} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontWeight: 700 }}>{g.name || t('cdg_unnamed')}</span>
                    <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                      {t('cdg_members', { n: g.members })}{g.createdByName ? ` · ${t('cdg_made_by', { name: g.createdByName })}` : ''}
                    </span>
                  </span>
                  {/* Where it sits among the pins — 1 is the top row of everybody's chat list. */}
                  {on && <span style={rank} title={t('cpn_place', { n: at + 1 })}>{at + 1}</span>}
                </label>
              );
            })}
            {groups.length === 0 && (
              <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', padding: '10px 0', borderTop: '1px solid var(--border)' }}>{t('cdg_no_groups')}</div>
            )}
            {full && groups.length > ids.length && (
              <div style={{ fontSize: 12, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 11px', marginTop: 10 }}>
                {t('cpn_full', { max })}
              </div>
            )}
          </>
        )}

        {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)', marginTop: 10 }}>{err}</div>}
        {done && !err && <div style={{ fontSize: 12, color: '#15803D', marginTop: 10 }}>{t('cdg_saved')}</div>}
      </div>
    </div>
  );
}
